import {categoryLabel, categoryPath, parseStockCategory} from "../../global/categories";
import React, {useEffect, useLayoutEffect, useState} from "react";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import axios from "../../util/axiosConfig";
import { copyUrl } from "../../util/copyUrl";
import "../../assets/css/component/board/BoardDetail.css";
import "../../assets/css/container/board/ViewPost.css";
import MiniTiptapEditor from "../../component/MiniTiptapEditor";
import {useAuth} from "../../global/AuthContext";
import CommentItem from "./boardClasses/CommentItem";
import LevelBadge from "../../global/LevelBadge";

type PostData = {
	id: number;
	title: string;
	content: string;
	category?: string;
	posCount: number;
	neuCount: number;
	negCount: number;
	viewCount: number;
	parentId?: number;
	parentTitle?: string;
	authorId?: number;
	mySentiment?: "positive" | "neutral" | "negative";
	authorNickname?: string;
	authorLevel: number;
	createdAt?: string;
	answerType: "positive" | "negative" | "neutral";
};

type Reply = {
	id: number;
	title: string;
	answerType: "positive" | "negative" | "neutral";
};

type Comment = {
	id: number;
	author: string;
	content: string;
	createdAt: string;
	authorNickname?: string;
	userId?: number;
	authorLevel: number;
};

type Params = {
	postId: string;
};

type Attachment = { id: number; originalName: string; contentType: string; sizeBytes: number };

function ViewPost() {
	const {postId} = useParams<Params>();
	const navigate = useNavigate();
	const location = useLocation();
	const fromPage = (location.state as { from?: string })?.from;
	const {user} = useAuth();

	const [post, setPost] = useState<PostData | null>(null);
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [comments, setComments] = useState<Comment[]>([]);
	const [newComment, setNewComment] = useState("");
	const [positiveReplies, setPositiveReplies] = useState<Reply[]>([]);
	const [negativeReplies, setNegativeReplies] = useState<Reply[]>([]);
	const [isInterested, setIsInterested] = useState(false);

	/* ===== 메타 태그 ===== */
	useEffect(() => {
		if (!post) return;

		document.title = `${post.title} - FactWeb`;

		const plainText = post.content
			.replace(/<[^>]+>/g, "")
			.replace(/\s+/g, " ")
			.trim()
			.slice(0, 150);

		let ogTitle = document.querySelector("meta[property='og:title']");
		if (!ogTitle) {
			ogTitle = document.createElement("meta");
			ogTitle.setAttribute("property", "og:title");
			document.head.appendChild(ogTitle);
		}
		ogTitle.setAttribute("content", post.title);

		let metaDesc = document.querySelector("meta[name='description']");
		if (!metaDesc) {
			metaDesc = document.createElement("meta");
			metaDesc.setAttribute("name", "description");
			document.head.appendChild(metaDesc);
		}
		metaDesc.setAttribute("content", plainText);

		let ogDesc = document.querySelector("meta[property='og:description']");
		if (!ogDesc) {
			ogDesc = document.createElement("meta");
			ogDesc.setAttribute("property", "og:description");
			document.head.appendChild(ogDesc);
		}
		ogDesc.setAttribute("content", plainText);

		let canonical = document.querySelector("link[rel='canonical']");
		if (!canonical) {
			canonical = document.createElement("link");
			canonical.setAttribute("rel", "canonical");
			document.head.appendChild(canonical);
		}
		canonical.setAttribute("href", `https://factweb.co.kr/viewPost/${post.id}`);

		let ogUrl = document.querySelector("meta[property='og:url']");
		if (!ogUrl) {
			ogUrl = document.createElement("meta");
			ogUrl.setAttribute("property", "og:url");
			document.head.appendChild(ogUrl);
		}
		ogUrl.setAttribute("content", `https://factweb.co.kr/viewPost/${post.id}`);
	}, [post]);

	/* ===== 데이터 로딩 ===== */
	useEffect(() => {
		axios.get(`/api/board/${postId}`).then(res => setPost(res.data));
		axios.get<Attachment[]>(`/api/board/${postId}/attachments`).then(res => setAttachments(res.data));
		axios.get(`/api/board/${postId}/comments`).then(res => setComments(res.data));
		axios.get(`/api/board/${postId}/replies`).then(res => {
			const replies: Reply[] = res.data;
			setPositiveReplies(replies.filter(r => r.answerType === "positive"));
			setNegativeReplies(replies.filter(r => r.answerType === "negative"));
		});
	}, [postId]);

	useEffect(() => {
		if (!user) return;
		axios.get(`/api/interest/check/${postId}`).then(res => {
			setIsInterested(res.data.interested);
		});
	}, [postId, user]);

	/* ===== SEO Render Complete ===== */
	useLayoutEffect(() => {
		if (!post?.content) return;

		let count = 0;
		const check = () => {
			const el = document.querySelector(".PostContent");
			if (el && el.textContent && el.textContent.trim().length > 30) {
				(window as any).renderComplete = true;
				return;
			}
			if (++count < 40) setTimeout(check, 200);
			else (window as any).renderComplete = true;
		};

		setTimeout(check, 300);
	}, [post]);

	/* ===== 액션 ===== */
	const handleAddComment = async () => {
		if (!user) return navigate("/login");
		if (!newComment.trim()) return;

		const res = await axios.post(`/api/board/${postId}/comments`, {
			content: newComment,
		});
		setComments(prev => [...prev, res.data]);
		setNewComment("");
	};

	const handleSentiment = async (sentiment: "positive" | "neutral" | "negative") => {
		if (!user) return navigate("/login");
		await axios.post(`/api/board/${postId}/sentiment`, null, { params: { sentiment } });
		const res = await axios.get(`/api/board/${postId}`);
		setPost(res.data);
	};

	const handleBack = () => {
		if (fromPage === "myfact") navigate("/myfact");
		else if (fromPage === "recentfact") navigate("/");
		else if (fromPage === "interestfact") navigate("/interestfact");
		else navigate("/");
	};

	const handleDelete = async () => {
		if (!window.confirm("정말 이 글을 삭제하시겠습니까?")) return;
		await axios.delete(`/api/board/delete/${post?.id}`);
		navigate("/");
	};

	const handleDeleteComment = async (id: number) => {
		if (!window.confirm("댓글을 삭제하시겠습니까?")) return;
		await axios.delete(`/api/board/comments/${id}`);
		setComments(prev => prev.filter(c => c.id !== id));
	};

	const handleUpdateComment = async (id: number, content: string) => {
		const res = await axios.put(`/api/board/comments/${id}`, { content });
		setComments(prev => prev.map(c => (c.id === id ? res.data : c)));
	};

	const handleCopyUrl = async () => {
		const url = new URL(`/viewPost/${postId}`, window.location.origin).href;
		try {
			await copyUrl(url);
			alert("URL이 복사되었습니다.");
		} catch {
			window.prompt("자동 복사를 사용할 수 없습니다. 아래 주소를 복사해 주세요.", url);
		}
	};

	const handleInterest = async () => {
		if (!user) return navigate("/login");

		if (isInterested) {
			await axios.delete(`/api/interest/remove/${post?.id}`);
			setIsInterested(false);
		} else {
			await axios.post(`/api/interest/add`, { postId: post?.id });
			setIsInterested(true);
		}
	};

	if (!post) return <div className="ViewPost BoardDetail">로딩중...</div>;

	const total = post.posCount + post.neuCount + post.negCount;
	const pct = (v: number) => (total > 0 ? (v * 100) / total : 0);

	return (
		<div className="ViewPost BoardDetail">

			<div className="PostContainer">

				<h1 className="PostTitle">{parseStockCategory(post.category) && `[${categoryLabel(post.category)}] `}{post.title}</h1>

				{post.category && (
					<div className="PostCategory">카테고리 : {categoryPath(post.category)}</div>
				)}

				{post.parentId && (
					<div className="ParentInfo">
						이 글은{" "}
						<a href={`/viewPost/${post.parentId}`} className="ParentLink">
							[{post.parentTitle || `글 #${post.parentId}`}]
						</a>{" "}
						의{" "}
						<strong>
							{post.answerType === "positive"
								? "긍정"
								: post.answerType === "negative"
									? "부정"
									: "중립"}
						</strong>{" "}
						답글입니다.
					</div>
				)}

				<div className="PostActions">
					<button onClick={handleBack}>목록</button>
					<button onClick={handleCopyUrl}>URL 복사</button>
					<button onClick={handleInterest}>
						{isInterested ? "관심해제" : "관심등록"}
					</button>
					{user?.id === post.authorId && (
						<button onClick={() => navigate(`/updatePost/${post.id}`)}>
							수정
						</button>
					)}
					{user?.id === post.authorId && (
						<button onClick={handleDelete}>삭제</button>
					)}
				</div>

				<div className="PostMeta">
					<LevelBadge level={post.authorLevel} />
					{post.authorNickname} ·{" "}
					{new Date(post.createdAt!).toLocaleString()} · 조회{" "}
					{post.viewCount}
				</div>

				{/* 🔥 본문 카드 */}
				<div className="PostContentCard">
					<div
						className="PostContent"
						dangerouslySetInnerHTML={{ __html: post.content }}
					/>
				</div>

				{/* 🔥 아래 영역 분리 */}
				{attachments.length > 0 && <section className="PostAttachments">
					{attachments.some(file => file.contentType.startsWith("image/")) && <div className="PostAttachmentImages">
						{attachments.filter(file => file.contentType.startsWith("image/")).map(file =>
							<figure key={file.id}>
								<img src={`/api/board/attachments/${file.id}/preview`} alt={file.originalName} loading="lazy" />
								<figcaption><a href={`/api/board/attachments/${file.id}/download`}>{file.originalName} 다운로드</a></figcaption>
							</figure>)}
					</div>}
					{attachments.some(file => !file.contentType.startsWith("image/")) && <>
						<h2>첨부파일</h2>
						<ul>{attachments.filter(file => !file.contentType.startsWith("image/")).map(file => <li key={file.id}>
							<a href={`/api/board/attachments/${file.id}/download`}>{file.originalName}</a>
							{" "}({Math.ceil(file.sizeBytes / 1024)} KB)
						</li>)}</ul>
					</>}
				</section>}
				<div className="PostInteractionSection">

					<div className="SentimentGauge">
						<div className="GaugeBar">
							<div className="GaugePos" style={{ width: `${pct(post.posCount)}%` }} />
							<div className="GaugeNeu" style={{ width: `${pct(post.neuCount)}%` }} />
							<div className="GaugeNeg" style={{ width: `${pct(post.negCount)}%` }} />
						</div>
						<div className="GaugeLabels">
							<span>긍정 {pct(post.posCount).toFixed(1)}%</span>
							<span>보류 {pct(post.neuCount).toFixed(1)}%</span>
							<span>부정 {pct(post.negCount).toFixed(1)}%</span>
						</div>
					</div>

					<div className="SentimentActions">
						<button
							className={post.mySentiment === "positive" ? "active" : ""}
							onClick={() => handleSentiment("positive")}
						>
							긍정 선택
						</button>
						<button
							className={post.mySentiment === "neutral" ? "active" : ""}
							onClick={() => handleSentiment("neutral")}
						>
							보류 선택
						</button>
						<button
							className={post.mySentiment === "negative" ? "active" : ""}
							onClick={() => handleSentiment("negative")}
						>
							부정 선택
						</button>
					</div>
				</div>
			</div>

			<div className="EvidenceActions">
				<button onClick={() => navigate(`/writeRep/${post.id}`, { state: { parentId: post.id, parentTitle: post.title, answerType: "positive" } })}>
					긍정 근거 작성
				</button>
				<button onClick={() => navigate(`/writeRep/${post.id}`, { state: { parentId: post.id, parentTitle: post.title, answerType: "negative" } })}>
					부정 근거 작성
				</button>
			</div>

			<div className="EvidenceContainer">
				<div className="EvidenceColumn">
					<h3>긍정 근거</h3>
					<ul>
						{positiveReplies.length === 0
							? <li>등록된 긍정 근거가 없습니다.</li>
							: positiveReplies.map(r => <li key={r.id}><a href={`/viewPost/${r.id}`}>{r.title}</a></li>)
						}
					</ul>
				</div>

				<div className="EvidenceColumn">
					<h3>부정 근거</h3>
					<ul>
						{negativeReplies.length === 0
							? <li>등록된 부정 근거가 없습니다.</li>
							: negativeReplies.map(r => <li key={r.id}><a href={`/viewPost/${r.id}`}>{r.title}</a></li>)
						}
					</ul>
				</div>
			</div>

			{/* 댓글 입력 */}
			<div className="CommentWrite">
				<MiniTiptapEditor
					content={newComment}
					onChange={setNewComment}
				/>

				<div className="CommentWriteActions">
					<button
						className="CommentSubmit"
						onClick={handleAddComment}
					>
						댓글 작성
					</button>
				</div>
			</div>

			<div className="CommentList">
				{comments.length === 0
					? <p className="NoComments">아직 댓글이 없습니다.</p>
					: comments.map(c => (
						<CommentItem
							key={c.id}
							comment={c}
							currentUserId={user?.id}
							onUpdate={handleUpdateComment}
							onDelete={handleDeleteComment}
						/>
					))
				}
			</div>
		</div>
	);
}

export default ViewPost;
